import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Alert, AlertTitle } from '@mui/material';
import styled from '@emotion/styled';
import MarkdownDisplay from './MarkdownDisplay';

const StyledAlert = styled(Alert)(({ theme }) => ({
    margin: theme.spacing(2),
    marginBottom: theme.spacing(0),
    display: 'flex',
    justifyContent: 'flex-start',
    width: '100%'
}));

const InlineAlert = styled(Alert)(({ theme }) => ({
    margin: 0,
    marginBottom: theme.spacing(2),
    display: 'flex',
    justifyContent: 'flex-start',
    width: '100%'
}));

const VALID_SEVERITIES = new Set(['info', 'success', 'warning', 'error']);

// setTimeout caps at ~24.8 days; reschedule in chunks for windows further out.
const MAX_TIMEOUT = 2147483647;

// Only the non-urgent severities can be dismissed; a warning or error stays put.
const DISMISSIBLE_SEVERITIES = new Set(['info', 'success']);

const ImportantMessage = ({ inline = false }) => {
    const message = useSelector((state) => state.config.message);
    const isLogged = useSelector((state) => state.auth.isLogged);
    const [tick, setTick] = useState(0);
    const [dismissedKey, setDismissedKey] = useState(null);

    const start = message?.startDate ? new Date(message.startDate).getTime() : NaN;
    const stop = message?.stopDate ? new Date(message.stopDate).getTime() : NaN;

    useEffect(() => {
        const now = Date.now();
        let next = null;
        if (Number.isFinite(start) && now < start) next = start;
        else if (Number.isFinite(stop) && now < stop) next = stop;
        if (next === null) return undefined;
        const timer = setTimeout(() => setTick((t) => t + 1), Math.min(next - now, MAX_TIMEOUT));
        return () => clearTimeout(timer);
    }, [start, stop, tick]);

    if (!message || !message.content) {
        return null;
    }

    const now = Date.now();
    const isMessageActive = Number.isFinite(start) && Number.isFinite(stop) && now >= start && now <= stop;

    if (!isMessageActive) {
        return null;
    }

    const AlertComponent = inline ? InlineAlert : StyledAlert;
    const severity = VALID_SEVERITIES.has(message.severity) ? message.severity : 'info';
    const subject = (message.subject && message.subject.trim()) || 'Important Notice';
    const messageKey = `${subject}|${severity}|${message.startDate}|${message.stopDate}|${message.content}`;
    const canDismiss = isLogged === true && DISMISSIBLE_SEVERITIES.has(severity);

    if (canDismiss && dismissedKey === messageKey) {
        return null;
    }

    return (
        <AlertComponent severity={severity} onClose={canDismiss ? () => setDismissedKey(messageKey) : undefined}>
            <AlertTitle>{subject}</AlertTitle>
            <MarkdownDisplay content={message.content} />
        </AlertComponent>
    );
};

export default ImportantMessage;
